import { useState } from "react";
import { Download, Smartphone, X, Share, Plus, MoreVertical, Monitor } from "lucide-react";
import useInstallPrompt from "@/hooks/useInstallPrompt";
import useAccessibleOverlay from "@/hooks/useAccessibleOverlay";

/**
 * InstallAppButton: drop-in button that always appears (unless the
 * app is already installed) and adapts its behaviour to the platform:
 *   • beforeinstallprompt available → trigger the native browser dialog
 *   • iOS Safari → show "Add to Home Screen" instructions
 *   • everything else → show a generic "How to install ConfirmPH" modal
 *
 * Hidden only when the app is already running standalone / installed
 * on this device, since the action would be a no-op there.
 */
export default function InstallAppButton({
  variant = "primary",       // primary | secondary | ghost
  size = "md",               // sm | md
  className = "",
  testId = "install-app-btn",
  label = "Install app",
}) {
  const { canInstall, installed, isIOS, promptInstall } = useInstallPrompt();
  const [modal, setModal] = useState(null); // null | "ios" | "generic"

  if (installed) return null;

  const base =
    "inline-flex items-center gap-2 rounded-lg font-medium whitespace-nowrap transition-colors";
  const sizes = size === "sm" ? "px-2.5 py-1.5 text-xs" : "px-3 py-2 text-sm";
  const variants = {
    primary:   "bg-[#1B4332] text-white hover:bg-[#143027]",
    secondary: "bg-white text-[#1B4332] border border-[#1B4332]/30 hover:bg-[#1B4332]/5",
    ghost:     "bg-transparent text-slate-700 hover:bg-slate-100 border border-slate-200",
  };
  const cls = `${base} ${sizes} ${variants[variant] || variants.primary} ${className}`;

  async function handleClick() {
    if (canInstall) {
      const r = await promptInstall();
      // If the browser didn't actually present a dialog (e.g. dismissed
      // without showing), fall back to generic instructions.
      if (r && r.outcome === "unavailable") setModal("generic");
      return;
    }
    if (isIOS) { setModal("ios"); return; }
    setModal("generic");
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={cls}
        data-testid={testId}
        aria-label={label}
      >
        <Download className="w-4 h-4" />
        <span>{label}</span>
      </button>

      {modal === "ios"     && <IosInstallModal     onClose={() => setModal(null)} />}
      {modal === "generic" && <GenericInstallModal onClose={() => setModal(null)} />}
    </>
  );
}

function ModalShell({ title, subtitle, onClose, testIdRoot, children }) {
  const { panelRef } = useAccessibleOverlay(true, onClose);
  const titleId = `${testIdRoot}-title`;
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 grid place-items-center p-4"
      onClick={onClose}
      data-testid={`${testIdRoot}-backdrop`}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className="bg-white w-full max-w-sm rounded-2xl shadow-2xl border border-slate-200"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid={`${testIdRoot}-modal`}
      >
        <div className="flex items-start justify-between p-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-[#1B4332] text-white grid place-items-center">
              <Smartphone className="w-4 h-4" />
            </div>
            <div>
              <div id={titleId} className="font-display font-semibold text-slate-900">{title}</div>
              <div className="text-xs text-slate-500">{subtitle}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 grid place-items-center rounded-md hover:bg-slate-100 text-slate-500"
            aria-label="Close"
            data-testid={`${testIdRoot}-close`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {children}

        <div className="p-4 pt-0">
          <button
            onClick={onClose}
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#1B4332] text-white text-sm font-medium hover:bg-[#143027]"
            data-testid={`${testIdRoot}-got-it`}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

function Step({ n, children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-700 grid place-items-center text-xs font-semibold flex-shrink-0">{n}</span>
      <span>{children}</span>
    </li>
  );
}

function IosInstallModal({ onClose }) {
  return (
    <ModalShell
      title="Install ConfirmPH"
      subtitle="Add to your iPhone home screen"
      onClose={onClose}
      testIdRoot="ios-install"
    >
      <ol className="p-4 space-y-3 text-sm text-slate-700">
        <Step n={1}>
          In Safari, tap the <span className="inline-flex items-center gap-1 align-middle"><Share className="w-3.5 h-3.5" /><strong>Share</strong></span> button in the toolbar.
        </Step>
        <Step n={2}>
          Scroll and tap <span className="inline-flex items-center gap-1 align-middle"><Plus className="w-3.5 h-3.5" /><strong>Add to Home Screen</strong></span>.
        </Step>
        <Step n={3}>
          Confirm the name and tap <strong>Add</strong>. ConfirmPH will appear on your home screen.
        </Step>
      </ol>
    </ModalShell>
  );
}

function GenericInstallModal({ onClose }) {
  return (
    <ModalShell
      title="Install ConfirmPH"
      subtitle="Add ConfirmPH to your device"
      onClose={onClose}
      testIdRoot="generic-install"
    >
      <div className="p-4 space-y-4 text-sm text-slate-700">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-1">
            <Smartphone className="w-3.5 h-3.5" /> On Android (Chrome)
          </div>
          <ol className="space-y-2">
            <Step n={1}>
              Open the <span className="inline-flex items-center gap-1 align-middle"><MoreVertical className="w-3.5 h-3.5" /><strong>menu</strong></span> in the top-right of Chrome.
            </Step>
            <Step n={2}>
              Tap <strong>Install app</strong> or <strong>Add to Home screen</strong>.
            </Step>
          </ol>
        </div>

        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-1">
            <Monitor className="w-3.5 h-3.5" /> On desktop (Chrome / Edge)
          </div>
          <ol className="space-y-2">
            <Step n={1}>
              Look for the <strong>install</strong> icon at the right edge of the address bar.
            </Step>
            <Step n={2}>
              Click it and confirm <strong>Install</strong>. ConfirmPH opens as its own app window.
            </Step>
          </ol>
        </div>

        <p className="text-xs text-slate-500">
          On iPhone? Use Safari → <strong>Share</strong> → <strong>Add to Home Screen</strong>.
        </p>
      </div>
    </ModalShell>
  );
}
